import { ProgrammeDetails } from "@customTypes/programmeDetails";
import { ProgrammeType } from "@customTypes/ProgrammeType";
import { TinyMceRichText } from "@customTypes/TinyMceRichText";
import clsx from "clsx";
import { ReactNode } from "react";
import Accordion from "./Accordion";
import styles from "./C11_VerticalAccordion.module.scss";

export interface AccordionItemData {
  title: ReactNode;
  content?: ReactNode;
  contentTinyMce?: TinyMceRichText;
  childContent?: ReactNode;
}

export interface Fields {
  accordionItems: {
    fields?: AccordionItemData;
    uid?: string;
  }[];
  singleDisplay?: boolean;
  defaultOpen: boolean;
  title?: string;
}

const INVISIBLE_MARKS_REGEX = /[\u200B-\u200F\uFEFF]/g;

const hasInvisibleWhiteSpace = (str) => {
  return INVISIBLE_MARKS_REGEX.test(str);
};

const cleanInvisibleWhiteSpace = (str) => {
  return str.replace(INVISIBLE_MARKS_REGEX, "").trim();
};

export interface AccordionData {
  metadata?: { tags: string[] };
  fields: Fields;
}

interface AccordionContainerProps {
  accordionFields: Fields;
  noHeading?: boolean;
  isDropdown?: boolean;
  isProgramme?: boolean;
  isCompare?: boolean;
  programme?: ProgrammeDetails;
  customClass?: string;
  requiresTitlePlaceholder?: boolean;
  hasBlueBackground?: boolean;
}

export const AccordionContainer = (props: AccordionContainerProps) => {
  const {
    noHeading,
    accordionFields,
    isProgramme,
    isCompare,
    isDropdown,
    customClass,
    programme,
    requiresTitlePlaceholder,
    hasBlueBackground,
  } = props;
  const { title } = accordionFields;
  const careerStageType =
    programme?.programmeType === ProgrammeType.EE
      ? programme?.programmeCareerStagesEeList
      : programme?.programmeCareerStagesDeList;
  const dataAttribute = isProgramme ? { "data-type": careerStageType } : null;
  const accordionProps = {
    ...accordionFields,
    isDropdown,
    isProgramme,
    isCompare,
    programme,
    hasBlueBackground,
  };

  const renderTitleContent = (
    title: string | undefined,
    requiresPlaceholder: boolean
  ) => {
    const trimmedTitle = title?.trim();

    if (trimmedTitle?.length) {
      if (hasInvisibleWhiteSpace(trimmedTitle)) {
        return (
          <div className="mb-[5.25rem]">
            {cleanInvisibleWhiteSpace(trimmedTitle)}
          </div>
        );
      }
      return <h3>{trimmedTitle}</h3>;
    }

    if (requiresPlaceholder) {
      return (
        <div className={"mb-auto md:mb-[5.25rem]"} aria-hidden="true"></div>
      );
    }

    return null;
  };

  const content = renderTitleContent(title, requiresTitlePlaceholder);

  return (
    <div
      {...dataAttribute}
      className={clsx(
        "component",
        styles["vertical-accordion"],
        customClass,
        isDropdown && styles["dropdown"],
        isProgramme && styles["programme"]
      )}
    >
      {noHeading ? (
        <Accordion {...accordionProps} />
      ) : (
        <div className="wrapper">
          {content}
          <Accordion {...accordionProps} />
        </div>
      )}
    </div>
  );
};

export default AccordionContainer;
